/**
 * 画面・PDF の書式（docs/design-system.md 5 章）。日時は JST（UTC+9、夏時間なし）。
 * Intl に頼らない（React Native の Hermes でも同じ結果にする）。
 */

const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"] as const;
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
export const NO_VALUE = "記載なし";
export const NO_PRICE = "価格未設定";

/** 0.5 は 0 から遠い側へ */
export function roundHalfAwayFromZero(v: number): number {
  return v < 0 ? -Math.floor(-v + 0.5) : Math.floor(v + 0.5);
}

function groupDigits(intStr: string): string {
  return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** 3 桁ごとにカンマ。decimals 桁に四捨五入 */
export function formatNumber(v: number, decimals = 0): string {
  if (!Number.isFinite(v)) return "";
  const scale = 10 ** decimals;
  const scaled = roundHalfAwayFromZero(Number((v * scale).toPrecision(15)));
  const neg = scaled < 0;
  const abs = Math.abs(scaled);
  const intPart = Math.floor(abs / scale);
  let s = groupDigits(String(intPart));
  if (decimals > 0) s += `.${String(abs - intPart * scale).padStart(decimals, "0")}`;
  return (neg ? "-" : "") + s;
}

function isNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/** "12,340,000円"。null → "価格未設定" */
export function formatYen(v: number | null | undefined): string {
  if (!isNum(v)) return NO_PRICE;
  return `${formatNumber(v)}円`;
}

function manPart(v: number): string {
  return formatNumber(v / 10_000);
}

/** "1,234万円"（万円未満は四捨五入） */
export function formatManYen(v: number): string {
  if (!isNum(v)) return NO_PRICE;
  return `${manPart(v)}万円`;
}

/** "1,080万〜1,420万円" */
export function formatYenRange(low: number, high: number): string {
  if (!isNum(low) || !isNum(high)) return NO_PRICE;
  return `${manPart(low)}万〜${manPart(high)}万円`;
}

/** "3,200 mm"。null → "記載なし" */
export function formatMm(v: number | null | undefined): string {
  if (!isNum(v)) return NO_VALUE;
  return `${formatNumber(v)} mm`;
}

/** "3.2 m"（小数1桁） */
export function formatM(vMm: number): string {
  if (!isNum(vMm)) return NO_VALUE;
  return `${formatNumber(vMm / 1000, 1)} m`;
}

/** "48.0 m²" */
export function formatArea(mm2: number): string {
  if (!isNum(mm2)) return NO_VALUE;
  return `${formatNumber(mm2 / 1_000_000, 1)} m²`;
}

/** "42.5 %" */
export function formatPercent(v: number): string {
  if (!isNum(v)) return NO_VALUE;
  return `${formatNumber(v, 1)} %`;
}

type JstParts = { year: number; month: number; day: number; weekday: number; hour: number; minute: number };

export function toJstParts(iso: string): JstParts | null {
  if (typeof iso !== "string" || iso.length === 0) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms + JST_OFFSET_MS);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    weekday: d.getUTCDay(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
  };
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** "2026年10月14日（火）"、時刻付きは "2026年10月14日（火） 17:00" */
export function formatDate(iso: string, withTime = false): string {
  const p = toJstParts(iso);
  if (!p) return NO_VALUE;
  const base = `${p.year}年${p.month}月${p.day}日（${WEEKDAYS[p.weekday]}）`;
  return withTime ? `${base} ${pad2(p.hour)}:${pad2(p.minute)}` : base;
}

/** "10/14 17:00" */
export function formatShortDateTime(iso: string): string {
  const p = toJstParts(iso);
  if (!p) return NO_VALUE;
  return `${p.month}/${p.day} ${pad2(p.hour)}:${pad2(p.minute)}`;
}
